/**
 * Where to send the user after sign-in, and helpers for farm onboarding vs dashboard.
 */

import { APP_HOME_PATH } from "../config/routes";

/** Internal path only — avoids open redirects from `location.state.from`. */
export function safeReturnPath(from) {
  if (!from || typeof from.pathname !== "string") return APP_HOME_PATH;
  const p = from.pathname;
  if (!p.startsWith("/") || p.startsWith("//")) return APP_HOME_PATH;
  if (p === "/login" || p === "/signup") return APP_HOME_PATH;
  const q =
    from.search && typeof from.search === "string" ? from.search : "";
  return `${p}${q}`;
}

export function isSuperAdminUser(user) {
  return Boolean(user?.roles?.includes?.("super_admin"));
}

export function hasFarmTenant(user) {
  const tid =
    user?.profile?.tenant_id !== undefined && user?.profile?.tenant_id !== null
      ? user.profile.tenant_id
      : user?.tenant_id;
  return tid != null && tid !== "";
}

/**
 * Farm operators must create/join an organization before the main app home.
 * Platform super admins may have no tenant and still use the app.
 */
export function needsCreateFarmOnboarding(user) {
  if (!user) return true;
  if (isSuperAdminUser(user)) return false;
  return !hasFarmTenant(user);
}

/**
 * Provisioned tenant admin: must complete farm welcome (name / invites) once.
 * Undefined profile flag is treated as complete so legacy users are not blocked.
 */
export function needsTenantWelcomeFlow(user) {
  if (!user || isSuperAdminUser(user)) return false;
  if (!hasFarmTenant(user)) return false;
  const done = user.profile?.tenant_first_login_setup_complete;
  if (done === undefined || done === null) return false;
  return done === false;
}

/**
 * After successful login: honor safe deep links, but never land no-tenant users on `/`.
 */
export function resolvePostLoginPath(user, fromLocation) {
  const needsFarm = needsCreateFarmOnboarding(user);
  const needsWelcome = needsTenantWelcomeFlow(user);

  if (!fromLocation?.pathname) {
    if (needsFarm) return "/create-farm";
    if (needsWelcome) return "/farm-setup";
    return APP_HOME_PATH;
  }

  const intended = safeReturnPath(fromLocation);

  if (needsFarm) {
    if (intended.startsWith("/create-farm")) return intended;
    return "/create-farm";
  }

  if (needsWelcome) {
    if (intended.startsWith("/farm-setup")) return intended;
    return "/farm-setup";
  }

  if (intended === "/login" || intended === "/signup") return APP_HOME_PATH;
  return intended;
}
